import React, { useEffect, useMemo, useState } from "react";

// Import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';
import { Button } from "reactstrap";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import { deleteWorkOrder, deleteWorkOrderRequest } from "store/actions";  
import { formatDateTimeToAmPm } from "helpers/dateFormat_helper";
import Cookies from "js-cookie";
import { MdDeleteOutline } from "react-icons/md";

const WorkOrderTable = ({ 
  List, 
  loading, 
  isPagination = true, 
  totalCount = 0, 
  pageIndex = 0, 
  pageSize = 10, 
  sorting = [], 
  onPaginationChange, 
  onSortingChange,
  onGlobalFilterChange
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [openModal, setOpenModal] = useState(false);
  const [deleteId, setDeleteId] = useState(false);
  const [confirmAction, setConfirmAction] = useState(false);
  const permissions2 = JSON.parse(localStorage?.getItem('permissions')) || [];
  const hasDeletePermission = permissions2.some(item => item?.permissionName === "Delete Work Orders");

  const handleDelete = (workOrderId) => {
    dispatch(deleteWorkOrder(workOrderId));
    setDeleteId('');
    setOpenModal(false);
    setConfirmAction(false);
  };

  useEffect(() => {
    if (deleteId && confirmAction) {
      handleDelete(deleteId);
    }
  }, [deleteId, confirmAction]);

  const handleNavigate = (workOrderId) => {
    Cookies.set('endDate', localStorage?.getItem('endDate'));
    Cookies.set('startDate', localStorage?.getItem('startDate'));
    Cookies.set('selectedTeam', localStorage?.getItem('selectedTeam'));
    Cookies.set('selectedStatus', localStorage?.getItem('selectedStatus'));
    Cookies.set('pageUrl', localStorage.getItem('pageUrl'));
    Cookies.set('selectedSolution', localStorage.getItem('selectedSolution'));
    Cookies.set('createdAt', localStorage.getItem('createdAt'));
    Cookies.set('sebelUserId', localStorage.getItem('sebelUserId'));
    navigate(`/workOrderDetails/${workOrderId}`, { state: { path: location.pathname } })
  }

  const columns = useMemo(
    () => [
      {
        header: 'Work Order Number',
        accessorKey: 'workOrderNumber',
        enableColumnFilter: false,
        enableSorting: false,
        
        sortIndex: 0,
        cell: ({ row }) => {
          const permissionNames = permissions2?.map(item => item?.permissionName) || [];
          const canClick = permissionNames.includes("Add Work Orders") ||
            permissionNames.includes("Feedback Link") ||
            permissionNames.includes("Mark As Completed") ||
            permissionNames.includes("Work Order Extension") ||
            permissionNames.includes("Work Order Details") ||
            permissionNames.includes("Edit Work Orders");
          return canClick ? (
            <span
              title="View Work Order"
              style={{ cursor: 'pointer', color: "rgb(4, 161, 182)" }}
              onClick={() => handleNavigate(row.original?.workOrderId)}
            >
              {row.original.workOrderNumber}
            </span>
          ) : (
            <span style={{ color: "#666" }}>
              {row.original.workOrderNumber}
            </span>
          );
        },
      },
      {
        header: 'Remedy Number',
        accessorKey: 'remedyNumber',
        enableColumnFilter: false,
        enableSorting: false,
        
        sortIndex: 1,
        cell: ({ row }) => (
          <span>
            {row.original.remedyNumber ? row.original.remedyNumber : 'N/A'}
          </span>
        ),
      },
      {
        header: 'Team',
        accessorKey: 'teamName',
        enableColumnFilter: false,
        enableSorting: false,
        
        sortIndex: 2,
      },
      {
        header: 'Customer Name',
        accessorKey: 'customerNameEng',
        enableColumnFilter: false,
        enableSorting: true,
        
        sortIndex: 3,
      },
      {
        header: 'Customer Phone Number',
        accessorKey: 'contactNumber1',
        enableColumnFilter: false,
        enableSorting: true,
        sortIndex: 4,
      },
      {
        header: 'Created By',
        accessorKey: 'createdBy',
        enableColumnFilter: false,
        enableSorting: true,
        sortIndex: 5,
      },
      {
        header: 'Area',
        accessorKey: 'areaName',
        enableColumnFilter: false,
        enableSorting: false,
        sortIndex: 6,
        cell: ({ row }) => (
          <span>
            {row.original.areaName ? row.original.areaName : 'N/A'}
          </span>
        ),
      },
      {
        header: 'Civil ID',
        accessorKey: 'contractId',
        enableColumnFilter: false,
        enableSorting: false,
        sortIndex: 7,
      },
      {
        header: 'Solution',
        accessorKey: 'solution',
        enableColumnFilter: false,
        enableSorting: false,
        sortIndex: 8,
        cell: ({ row }) => (
          <span>
            {row?.original?.solutionName}
          </span>
        ),
      },
      {
        header: 'Created At',
        accessorKey: 'createdAt',
        enableColumnFilter: false,
        enableSorting: true,
        sortIndex: 9,
        cell: ({ row }) => (
          <span>
            {formatDateTimeToAmPm(row.original.createdAt)}
          </span>
        ),
      },
      {
        header: 'Scheduled Start Date',
        accessorKey: 'scheduledStartDateTime',
        enableColumnFilter: false,
        enableSorting: true,
        sortIndex: 10,
        cell: ({ row }) => (
          <span>
            {formatDateTimeToAmPm(row.original.scheduledStartDateTime)}
          </span>
        ),
      },
      {
        header: 'Scheduled End Date',
        accessorKey: 'scheduledEndDateTime',
        enableColumnFilter: false,
        enableSorting: true,
        sortIndex: 11,
        cell: ({ row }) => (
          <span>
            {formatDateTimeToAmPm(row.original.scheduledEndDateTime)}
          </span>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'workOrderStatus',
        enableColumnFilter: false,
        enableSorting: false,
        
        sortIndex: 12,
        cell: ({ getValue }) => getValue() || 'N/A',
      },
      // Conditionally render Actions column based on permission
      ...(hasDeletePermission
        ? [
          {
            header: 'Actions',
            id: 'actions', // Unique id for this column
            cell: ({ row }) => {
              return (
                <div className="d-flex gap-2">
                  <Button
                    color="danger"
                    onClick={() => { setDeleteId(row?.original?.workOrderId); setOpenModal(true); }}
                    className="mr-2"
                  >
                    <MdDeleteOutline size={18} />
                  </Button>
                </div>
              );
            },
          },
        ]
        : []),
    ],
    [hasDeletePermission]
  );

  // Meta title for the page
  document.title = "Work Orders | Your Admin Dashboard";

  return (
    <>
      <ConfirmationModal
        okText={"Confirm"}
        onCancel={() => { setDeleteId(''); setOpenModal(false); }}
        onOk={() => { setConfirmAction(true); }}
        isVisible={openModal}
        title="Delete Work Order"
        content="Are you sure you want to delete this work order?"
      />
      <div className="">
        <div className="container-fluid">
          {/* <Breadcrumbs title="Tables" breadcrumbItem="Work Orders" /> */}

          <TableContainer
            loading={loading}
            columns={columns}
            data={List || []}
            isGlobalFilter={false}
            isPagination={isPagination}
            SearchPlaceholder="Search..."
            pagination="pagination"
            docName="Work Orders"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
            manualPagination={true}
            manualSorting={true}
            manualFiltering={true}
            totalCount={List && List.length > 0 ? List[0]?.TotalCount : totalCount}
            pageCount={List && List.length > 0 ? Math.ceil(List[0]?.TotalCount / pageSize) : Math.ceil(totalCount / pageSize)}
            onPaginationChange={onPaginationChange}
            onSortingChange={onSortingChange}
            onGlobalFilterChange={onGlobalFilterChange}
            paginationState={{ pageIndex, pageSize }}
            sortingState={sorting}
          />
        </div>
      </div>
    </>
  );
};

export default WorkOrderTable;
